import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './HomeLanding.css';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import ShareRow from '../../components/ShareRow/ShareRow';
import { usePublicReviews } from '../../components/Testimonials/Testimonials';
import { SITE_LANGUAGE_LANDINGS, SITE_LANGUAGE_ALTERNATES } from '../../data/siteLanguages';
import { browserSiteLanguage } from '../../data/browserLanguage';
import { HUB_PATHS } from '../../data/cardMeanings';
import HomeHeroCards from './HomeHeroCards';
import MemberHistory from '../../components/MemberHistory/MemberHistory';
import { useAuth } from '../../contexts/AuthContext';
import { chartHref } from '../../data/chartLanguage';
import { noteHomeClick, noteHomeEvent } from '../../services/homeAnalytics';
import { cardOfTheDayPath, rememberSpreadLanguage } from './homeLinks';
import { LEARN_TAROT_ENABLED } from '../LearnTarot/learnData';

const FEATURED_LANGUAGES = 6;

// The saved homepage lists the same six languages for every visitor. A supported
// browser language is moved to the front only after that first draw, so the
// prerendered page and the first client render still match.
function featuredLanguages(languages, leadCode) {
  const featured = languages.slice(0, FEATURED_LANGUAGES);
  if (!leadCode || featured.some((language) => language.code === leadCode)) return featured;
  const match = languages.find((language) => language.code === leadCode);
  if (!match) return featured;
  return [match, ...featured.slice(0, FEATURED_LANGUAGES - 1)];
}

const NO_SPACE_LANGS = new Set(['zh', 'ja', 'th']);

const ENGLISH_LINK = { code: 'en', path: '/', flag: '🌐', seoLinkLabel: 'AI Tarot in English' };

const GROUPS = [
  {
    id: 'tarot',
    glyph: '✦',
    tone: 'yellow',
    links: [
      { id: 'freeTarot', to: '/free-tarot/' },
      { id: 'yesNo', to: '/yes-or-no-tarot/' },
      { id: 'threeCard', to: '/three-card-tarot/' },
      { id: 'daily', to: '/daily-tarot-reading/' },
      { id: 'cardOfTheDay', to: '/card-of-the-day/' },
      { id: 'career', to: '/career-tarot-reading/' },
      { id: 'mindBodySpirit', to: '/mind-body-spirit-tarot/' },
      { id: 'sixCard', to: '/six-card-tarot-spread/' },
      { id: 'celtic', to: '/celtic-cross-tarot/' },
      { id: 'interpreter', to: '/tarot-card-interpreter/', isNew: true },
    ],
  },
  {
    id: 'love',
    glyph: '♥',
    tone: 'pink',
    links: [
      { id: 'love', to: '/love-tarot-reading/' },
      { id: 'zodiac', to: '/zodiac-compatibility/', isNew: true },
      { id: 'chartCompat', to: '/birth-chart-compatibility/', isNew: true },
      { id: 'venus', to: '/venus-sign-calculator/', isNew: true },
      { id: 'newLove', to: '/new-love-tarot-reading/' },
    ],
  },
  {
    id: 'astrology',
    glyph: '☉',
    tone: 'green',
    links: [
      { id: 'birthChart', to: '/birth-chart/', isNew: true },
      { id: 'dailyHoroscope', to: '/personalized-daily-horoscope/', isNew: true },
      { id: 'bigThree', to: '/big-three-calculator/', isNew: true },
      { id: 'risingSign', to: '/rising-sign-calculator/', isNew: true },
      { id: 'moonSign', to: '/moon-sign-calculator/', isNew: true },
      { id: 'moonPhase', to: '/moon-phase-today/', isNew: true },
      { id: 'lifePath', to: '/life-path-number-calculator/', isNew: true },
      { id: 'birthCard', to: '/tarot-birth-card-calculator/', isNew: true },
    ],
  },
];

const TAROT_SPREAD_LINKS = new Set(['freeTarot', 'yesNo', 'threeCard', 'daily', 'career', 'mindBodySpirit', 'sixCard', 'celtic', 'love', 'newLove']);

const LanguageLink = ({ language }) => (
  <a
    href={language.path}
    lang={language.code}
    dir={language.code === 'ar' ? 'rtl' : undefined}
  >
    <span aria-hidden="true">{language.flag}</span> {language.seoLinkLabel}
  </a>
);

function linkTarget(link, lang) {
  if (link.id === 'cardOfTheDay') return cardOfTheDayPath(lang);
  if (link.id === 'birthChart' || link.id === 'chartCompat' || link.id === 'interpreter' || link.id === 'dailyHoroscope') return chartHref(link.to, lang);
  return link.to;
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="#2D3436" strokeWidth="2" />
      <path d="M12 3.5v17M3.5 12h17" stroke="#2D3436" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="1.7" fill="#2D3436" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M12 18.6s-5.6-3.4-5.6-7.4C6.4 8.6 8 7.2 9.7 7.2c1.1 0 2 .5 2.3 1.4.3-.9 1.2-1.4 2.3-1.4 1.7 0 3.3 1.4 3.3 4 0 4-5.6 7.4-5.6 7.4z"
        fill="#2D3436"
      />
    </svg>
  );
}

const LINK_FALLBACKS = {
  dailyHoroscope: {
    title: 'Your Astrology Today',
    text: 'Today’s sky, read against your birth chart',
  },
};

const HomePage = ({ copy, lang = 'en', dir, head, jsonLd = [], ask = {} }) => {
  const { user } = useAuth();
  const reviews = usePublicReviews();
  const isEnglish = lang === 'en';
  const pagePath = isEnglish ? '/' : `/${lang}/`;
  const others = SITE_LANGUAGE_LANDINGS.filter((language) => language.code !== lang);
  const languages = isEnglish ? others : [ENGLISH_LINK, ...others];
  const [leadCode, setLeadCode] = useState(null);
  useLayoutEffect(() => {
    const code = browserSiteLanguage();
    setLeadCode(code !== 'en' && code !== lang ? code : null);
  }, [lang]);
  const featured = featuredLanguages(languages, leadCode);
  const quotes = reviews.slice(0, 2);
  const onAsk = useCallback(() => noteHomeEvent('home_ask_submit', { language: lang }), [lang]);
  const onAskComplete = useCallback(() => noteHomeEvent('home_ask_complete', { language: lang }), [lang]);

  const faqJsonLd = {
    "@type": "FAQPage",
    "inLanguage": lang,
    "mainEntity": copy.faq.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  return (
    <div className="landing-page hl-page" lang={isEnglish ? undefined : lang} dir={dir}>
      <Helmet>
        {head}
        {SITE_LANGUAGE_ALTERNATES.map((alternate) => (
          <link
            key={alternate.hrefLang}
            rel="alternate"
            hrefLang={alternate.hrefLang}
            href={alternate.href}
          />
        ))}
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@graph": [...jsonLd, faqJsonLd] })}
        </script>
      </Helmet>
      <Header />

      <main>
        <section className="hl-hero">
          <div className="hl-wrap hl-hero-grid">
            <div className="hl-ask-card">
              <span className="hl-eyebrow">{copy.eyebrow}</span>
              <h1>
                {copy.headingBefore}{NO_SPACE_LANGS.has(lang) ? '' : ' '}<em>{copy.headingHighlight}</em>
              </h1>
              <p className="hl-sub">
                <span className="hl-sub-long">{copy.sub}</span>
                {copy.subShort && <span className="hl-sub-short">{copy.subShort}</span>}
              </p>
              <AiAskBox
                autoFocus
                {...ask}
                moreLabel={copy.moreExamples}
                onAsk={onAsk}
                onComplete={onAskComplete}
              />
              <ul className="hl-trust">
                {copy.trust.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <HomeHeroCards copy={copy} lang={lang} />
            <nav className="hl-langs" aria-label={copy.langsLabel}>
              <span className="hl-langs-label">{copy.langsLabel}</span>
              {featured.map((language) => <LanguageLink key={language.path} language={language} />)}
              <details className="hl-langs-more">
                <summary>{copy.langsAll.replace('{n}', languages.length)}</summary>
                <div className="hl-langs-grid">
                  {languages.map((language) => <LanguageLink key={language.path} language={language} />)}
                </div>
              </details>
            </nav>
          </div>
        </section>

        {user && <MemberHistory />}

        {copy.beyond && (
          <section className="hl-wrap hl-band hl-beyond" aria-labelledby="hl-beyond">
            <h2 id="hl-beyond">{copy.beyond.title}</h2>
            <div className="hl-beyond-grid">
              <a
                className="hl-beyond-card"
                href={chartHref('/birth-chart/', lang)}
                onClick={() => noteHomeClick('birthChart', 'beyond', lang)}
              >
                <span className="hl-glyph hl-green"><ChartIcon /></span>
                <h3>{copy.beyond.birthTitle}</h3>
                <p>{copy.beyond.birthText}</p>
                <span className="hl-beyond-cta">{copy.beyond.birthCta}</span>
              </a>
              <a
                className="hl-beyond-card"
                href={chartHref('/birth-chart-compatibility/', lang)}
                onClick={() => noteHomeClick('chartCompat', 'beyond', lang)}
              >
                <span className="hl-glyph hl-pink"><HeartIcon /></span>
                <h3>{copy.beyond.compatTitle}</h3>
                <p>{copy.beyond.compatText}</p>
                <span className="hl-beyond-cta">{copy.beyond.compatCta}</span>
              </a>
            </div>
          </section>
        )}

        <section className="hl-wrap hl-band" aria-labelledby="hl-explore">
          <h2 id="hl-explore">{copy.exploreTitle}</h2>
          <p className="hl-lede">{copy.exploreLede}</p>
          <div className="hl-groups">
            {GROUPS.map((group) => (
              <div className="hl-group" key={group.id}>
                <h3><span className={`hl-glyph hl-${group.tone}`} aria-hidden="true">{group.glyph}</span>{copy.groups[group.id]}</h3>
                <ul>
                  {group.links.map((link) => {
                    const item = copy.links[link.id] || LINK_FALLBACKS[link.id];
                    if (!item) return null;
                    return (
                    <li key={link.id}>
                      <a
                        href={linkTarget(link, lang)}
                        onClick={() => {
                          if (TAROT_SPREAD_LINKS.has(link.id)) rememberSpreadLanguage(lang);
                          noteHomeClick(link.id, 'directory', lang);
                        }}
                      >
                        <span>
                          {item.title}
                          <small>{item.text}</small>
                        </span>
                        {link.isNew && <span className="hl-new">{copy.newBadge}</span>}
                      </a>
                    </li>
                    );
                  })}
                </ul>
                {!isEnglish && group.id === 'astrology' && <p className="hl-english-note">{copy.englishNote}</p>}
              </div>
            ))}
          </div>
          {isEnglish && LEARN_TAROT_ENABLED && (
            <a className="hl-learn-link" href="/learn-tarot/" onClick={() => noteHomeClick('learnTarot', 'directory', lang)}>
              <span>
                <strong>New to Tarot? Start Learning</strong>
                <small>Explore free guides to tarot cards, spreads, history, and interpretation.</small>
              </span>
              <span className="hl-learn-cta">Explore Learn Tarot →</span>
            </a>
          )}
        </section>

        <section className="hl-wrap hl-band" aria-labelledby="hl-how">
          <h2 id="hl-how">{copy.howTitle}</h2>
          <p className="hl-lede">{copy.howLede}</p>
          <ol className="hl-steps">
            {copy.steps.map((step, index) => (
              <li key={step.title}>
                <span className="hl-step-num">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="hl-wrap hl-band" aria-labelledby="hl-proof">
          <div className={`hl-proof${quotes.length ? '' : ' hl-proof-solo'}`}>
            <div className="hl-accuracy">
              <h2 id="hl-proof">{copy.proofTitle}</h2>
              <p>{copy.proofText}</p>
              <p className="hl-stats">
                {copy.stats.map((stat) => <span key={stat}>{stat}</span>)}
              </p>
              <a href="/how-it-works/">{copy.proofLink}</a>
            </div>
            {quotes.length > 0 && (
              <div className="hl-quotes" lang="en">
                {quotes.map((review, index) => (
                  <figure className="hl-quote" key={`${review.display_name}-${index}`}>
                    <span className="hl-stars" aria-label={`${review.rating} / 5`}>{'★'.repeat(review.rating || 5)}</span>
                    <blockquote>“{review.review_text}”</blockquote>
                    {review.display_name && <figcaption>{review.display_name}</figcaption>}
                  </figure>
                ))}
              </div>
            )}
          </div>

          <div className="hl-share">
            <div>
              <h2>{copy.shareTitle}</h2>
              <p>{copy.shareText}</p>
            </div>
            <ShareRow
              url={pagePath}
              text={copy.shareMessage}
              label=""
              compact
              shareLabel={copy.shareButton}
              copyLabel={copy.copyLink}
              copiedLabel={copy.copied}
            />
          </div>
        </section>

        <section className="hl-wrap hl-band" aria-labelledby="hl-faq">
          <h2 id="hl-faq">{copy.faqTitle}</h2>
          <div className="hl-faq">
            {copy.faq.map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
          <p className="hl-more">
            {copy.moreQuestions} <a href="/how-it-works/">{copy.moreHow}</a>
            {' · '}
            <a href={HUB_PATHS[lang] || HUB_PATHS.en}>{copy.moreMeanings}</a>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
